import { useCallback, useContext } from 'react';
import useLog from '../../useLog/useLog';
import useSwitchesFromContext from '../../../hooks/useSwitchesFromContext';
import CheckoutPageContext from '../../../context/CheckoutPageContext';
import $window from '../../../tools/window';

const getRandomBytes = (size) => {
  const buffer = new Uint8Array(size);
  crypto.getRandomValues(buffer);
  return Array.from(buffer).map((value) => value.toString(16)).join('');
};

const requestAddressVerification = ({ address, logger }) => {
  const requestID = getRandomBytes(16);
  const eventName = `addressVerification:mfe:response:${requestID}`;

  logger.log('requestAddressVerification', address);

  return new Promise((resolve, reject) => {
    const handleResponse = (event) => {
      const { error, data } = event.detail || {};

      if (error) {
        logger.log('verification error', error.message);
        reject(error);
      } else {
        logger.log('verification success', data);
        resolve({ ...data, requestID });
      }
    };

    $window.addEventListener(eventName, handleResponse, { once: true });

    $window.dispatchEvent(new CustomEvent('addressVerification:request', {
      detail: { address, requestID },
    }));
  });
};

/**
 * Custom hook to verify address using Checkout MFE
 */
const useAddressVerification = () => {
  const logger = useLog('checkout.addressVerification');
  const {
    checkoutPageState,
  } = useContext(CheckoutPageContext);
  const {
    cartPayment, bagItems, shipMethod, selectedPaymentType,
  } = checkoutPageState;
  const { getSwitchValue } = useSwitchesFromContext();
  const isCartVerified = getSwitchValue('isCartVerifiedEnabled');
  const isFeatureEnabled = getSwitchValue('chkAddrVerifyEnabled');

  const verifyAddress = useCallback(async (address) => {
    logger.log('verifyAddress', address);

    if (!address) {
      logger.log('invalid address');
      return {
        address,
        editAddress: true,
        error: new Error('invalid address'),
        reason: 'invalid address',
      };
    }
    if (isCartVerified && address.isAddressVerified) {
      logger.log('address already verified');
      return {
        address,
        reason: 'address already verified',
        useEnteredAddress: true,
      };
    }

    if (cartPayment) {
      logger.log('express lane not supported');
      return {
        address,
        reason: 'not enabled',
        useEnteredAddress: true,
      };
    }

    if (!isFeatureEnabled) {
      logger.log('feature switch off');
      return {
        address,
        reason: 'not enabled',
        useEnteredAddress: true,
      };
    }

    if (['klarna', 'applepay', 'googlepay'].includes(selectedPaymentType)) {
      logger.log('unsupported payment method');
      return {
        address,
        reason: 'not enabled',
        useEnteredAddress: true,
      };
    }

    if (bagItems?.hasOnlyEGiftCard) {
      logger.log('eGiftCard only purchase not supported');
      return {
        address,
        reason: 'not enabled',
        useEnteredAddress: true,
      };
    }

    if (shipMethod?.shipModeType?.toLowerCase() === 'directtodesk') {
      logger.log('ship method not supported');
      return {
        address,
        reason: 'not enabled',
        useEnteredAddress: true,
      };
    }

    try {
      const result = await requestAddressVerification({ address, logger });
      return {
        ...result,
        address,
      };
    } catch (error) {
      logger.log('verification failed or MFE unavailable', error);
      return {
        address,
        error,
        reason: error.message || 'service unavailable',
        useEnteredAddress: true,
      };
    }
  }, [logger, isCartVerified, cartPayment, isFeatureEnabled,
    shipMethod?.shipModeType, selectedPaymentType, bagItems?.hasOnlyEGiftCard]);

  return {
    verifyAddress,
  };
};

export default useAddressVerification;
